import { useState, type FormEvent } from "react";
import { comicCategories } from "@/mocks/comics";

const FORM_ADDR = "https://readdy.ai/api/form/da2msebd3r93mn1k3cb0";

type Status = "idle" | "loading" | "success" | "error";

export default function Subscribe() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string) || "";
    if (honeypot.trim()) {
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }

    const raw = formData.get("message") as string;
    if (raw && raw.length > 500) {
      setStatus("error");
      setFormError("留言内容请控制在 500 字以内");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch (err) {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <section id="subscribe" className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="overflow-hidden rounded-lg border border-primary-600/40 bg-background-900/70">
          <div className="grid lg:grid-cols-2">
            <div className="relative p-6 md:p-10">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-400">
                <i className="ri-mail-send-line"></i>
                更新订阅
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
                订阅更新，<span className="text-gradient-gold">好漫不错过</span>
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-400">
                留下你的邮箱与偏好分类，热门韩漫上新、VIP 章节解锁与会员活动将第一时间推送到你手中。
              </p>

              <ul className="mt-6 flex flex-col gap-3">
                {[
                  "热门连载每日更新推送",
                  "VIP 章节解锁与抢先看提醒",
                  "尊享会员专属活动与福利",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-foreground-300">
                    <i className="ri-checkbox-circle-fill text-primary-400"></i>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-background-700/60 bg-background-950/40 p-6 md:p-10 lg:border-l lg:border-t-0">
              <form
                data-readdy-form="da2msebd3r93mn1k3cb0"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
                noValidate
              >
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-200">
                    常用邮箱 <span className="text-accent-400">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-600 bg-background-900/70 px-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-200">
                    昵称（选填）
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    placeholder="如何称呼你"
                    className="w-full rounded-md border border-background-600 bg-background-900/70 px-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-200">
                    偏好的漫画分类
                  </label>
                  <select
                    id="category"
                    name="category"
                    defaultValue=""
                    className="w-full rounded-md border border-background-600 bg-background-900/70 px-4 py-3 text-sm text-foreground-100 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="" disabled>
                      请选择分类
                    </option>
                    <option value="全部">全部（都喜欢）</option>
                    {comicCategories.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 flex items-center justify-between text-sm font-medium text-foreground-200">
                    <span>想推荐的漫画（选填）</span>
                    <span className="text-xs text-foreground-500">{message.length}/500</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="告诉我们你期待的漫画或建议……"
                    className="w-full resize-none rounded-md border border-background-600 bg-background-900/70 px-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-1 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? "提交中…" : "订阅更新通知"}
                </button>

                {status === "success" && (
                  <p className="rounded-md border border-primary-500/50 bg-primary-500/10 px-4 py-3 text-sm text-primary-200">
                    <i className="ri-checkbox-circle-fill mr-1.5"></i>
                    订阅成功，感谢你的支持，最新漫画更新将第一时间送达！
                  </p>
                )}
                {status === "error" && (
                  <p className="rounded-md border border-accent-500/50 bg-accent-500/10 px-4 py-3 text-sm text-accent-200">
                    <i className="ri-error-warning-fill mr-1.5"></i>
                    {formError || "提交失败，请稍后重试"}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}